import { z } from "zod";
import { SUPPORT_CATEGORIES, type SupportCategory } from "./support.js";

/**
 * 問い合わせの分類の体系（#148、docs/58 §10）。ここ1か所が正本：
 *   - AI（mai-11）に渡す定義（管理アプリの処理役がこの表から文を作る）
 *   - 管理アプリの選択肢・札・当たり率の集計
 *   - 利用者のフォームの選択肢（種類だけ。support.ts の SUPPORT_CATEGORIES）
 * 軸は 製品（自動）／種類（利用者 → AI の見込み → 運営が確定）／機能（画面から自動）／緊急度／必要なもの、完了のときに 解決のしかた。
 * コードは一度決めたら変えない（変えると過去の集計が切れる）。分けたいときは新しいコードを足し、古いコードは残す（§10.3.1）
 */

// ---------------------------------------------------------------------------
// 種類（7つ）：入るもの・入らないもの（§10.2）
// ---------------------------------------------------------------------------
export const SUPPORT_CATEGORY_DEFS: Record<SupportCategory, { includes: string; excludes: string }> = {
  usage: { includes: "やり方が分からない・どこにあるか", excludes: "思った動きをしない（不具合か使い方かは運営が確定する）" },
  bug: { includes: "エラーが出る・表示が崩れる・計算が合わない・PDF が出ない・前はできた", excludes: "機能が無い（→ 要望）" },
  request: { includes: "機能がほしい・変えてほしい・言葉を変えたい", excludes: "" },
  billing: { includes: "プラン・お支払い・運営から利用者への領収書・解約", excludes: "利用者が出す請求書の使い方（→ 使い方）" },
  account: { includes: "ログインできない・パスワード・ユーザー追加・案内メール（招待）・会社の切替・確認コード", excludes: "" },
  data: { includes: "移行・持ち出し・消した・戻したい・退会", excludes: "" },
  other: { includes: "営業・取材・お礼・どれでもない", excludes: "" },
};
export const SUPPORT_CATEGORY_CODES = Object.keys(SUPPORT_CATEGORIES) as SupportCategory[];

// ---------------------------------------------------------------------------
// 機能（16の束。§10.3）。束を足すときは表の最後に足し、下の SUPPORT_FEATURE_ROUTES にもパスを足す（足し忘れは Vitest が落とす）
// ---------------------------------------------------------------------------
export const SUPPORT_FEATURES = {
  home: { label: "ホーム", about: "ホーム（本日の業務・今月の実績）" },
  estimate: { label: "見積書", about: "見積書の一覧・新規・紙の画面・見積書の比較・見積書のテンプレート" },
  invoice: { label: "請求書", about: "請求書（分割請求を含む）" },
  receipt: { label: "領収書", about: "領収書" },
  payment: { label: "入金", about: "入金と消込" },
  group: { label: "案件", about: "案件の一覧・案件の画面（地図を含む）" },
  schedule: { label: "工程表", about: "工程表・全体の工程表・協力会社のページ" },
  vendor: { label: "外注見積・見積依頼", about: "外注見積の登録と取込・見積依頼書・注文書（発注書）と注文請書の承諾（#233）・仕入と支払予定（#233 3a）" },
  partner: { label: "顧客", about: "顧客の一覧・詳細・支店・担当者" },
  paper: { label: "紙・PDF・帳票デザイン", about: "どの帳票でも印刷・PDF の話・帳票デザイン" },
  settings: { label: "設定", about: "自社情報・振込口座・端数処理・言葉・使う機能 ほか" },
  users: { label: "ユーザー・会社", about: "ユーザー管理・会社を選ぶ・個人設定" },
  login: { label: "ログイン・会員登録", about: "ログイン・会員登録・パスワードの再設定・案内メールからの利用開始（招待）・Google／Apple・パスキー" },
  plan: { label: "料金プラン・お支払い", about: "料金プラン・お支払い・特定商取引法の表記" },
  mail: { label: "メール", about: "メールが届かない・迷惑メール（画面に依らない）" },
  other: { label: "その他・分からない", about: "どれにも当てはまらない" },
} as const;
export type SupportFeature = keyof typeof SUPPORT_FEATURES;
export const SUPPORT_FEATURE_CODES = Object.keys(SUPPORT_FEATURES) as SupportFeature[];
export const SupportFeature = z.enum(SUPPORT_FEATURE_CODES as [SupportFeature, ...SupportFeature[]]);

/**
 * パス → 機能（§10.3。開発に渡すの SCREEN_GUIDE と同じ考え方）。画面のパスは id を :id・:n に伏せた形（apps/web の screenPath）。
 * 上から順に見て最初に合ったもの。null＝画面に依らない（お問い合わせのページ・返事のリンク）＝AI が本文から選ぶ。
 * 新しい画面を作ったらここに1行足す（apps/api/test/support-features.test.ts が、どの束にも入らない画面を落とす）
 */
export const SUPPORT_FEATURE_ROUTES: readonly (readonly [RegExp, SupportFeature | null])[] = [
  // 紙（どの帳票でも「印刷・PDF」の話はここ）
  [/\/paper(\/|$)/, "paper"],
  [/^\/settings\/paper(\/|$)/, "paper"],
  // 送付状（#223）：設定 → 送付状・単体送付状＝印刷・PDF の話
  [/^\/settings\/cover-letters(\/|$)/, "paper"],
  [/^\/cover-letters(\/|$)/, "paper"],
  // 送付の記録の PDF の画面（#223 の3回目の前の直し＝★18）
  [/^\/deliveries(\/|$)/, "paper"],
  // メール送付（#225）：{customer}が開く閲覧ページ（ログインなし）・設定 → 見積・請求 → メールの文面
  [/^\/d(\/|$)/, "paper"],
  [/^\/settings\/estimate\/mail(\/|$)/, "paper"],
  // 設定の中で別の束に入るもの（先に見る）
  [/^\/settings\/templates(\/|$)/, "estimate"],
  [/^\/settings\/schedule-(templates|calendar)(\/|$)/, "schedule"],
  [/^\/settings\/rfq(\/|$)/, "vendor"],
  // 注文書（#233）：設定 → {order}・{vendor}が開く承諾ページ（ログインなし）
  [/^\/settings\/orders(\/|$)/, "vendor"],
  [/^\/o\//, "vendor"],
  // 支払通知書の確認ページ（#233 3b）：{vendor}がログインせずに開く1枚＝仕入・{order}と同じ束
  [/^\/n\//, "vendor"],
  // データの取込（#268 1.5c-1）：未入金の請求書＝請求の束
  [/^\/imports(\/|$)/, "invoice"],
  [/^\/settings\/imports(\/|$)/, "invoice"],
  [/^\/settings\/users(\/|$)/, "users"],
  [/^\/settings\/login-methods(\/|$)/, "login"],
  [/^\/settings(\/|$)/, "settings"],
  [/^\/home(\/|$)/, "home"],
  [/^\/estimates(\/|$)/, "estimate"],
  [/^\/invoices(\/|$)/, "invoice"],
  // 締め請求書（#267、docs/97）：月ごとの締め＝「請求書」の束
  [/^\/closing(\/|$)/, "invoice"],
  [/^\/recurring(\/|$)/, "invoice"],
  // {deliveryNote}（#266、docs/96）：請求の手前の帳票＝束ね請求・税を確定する書類と一緒に「請求書」の束（束のコードは足さない）
  [/^\/delivery-notes(\/|$)/, "invoice"],
  // {acceptance}（#266 1b）と受領・検収のページ（{customer}がログインせずに開く1枚）も同じ「請求書」の束
  [/^\/acceptances(\/|$)/, "invoice"],
  [/^\/a\//, "invoice"],
  [/^\/receipts(\/|$)/, "receipt"],
  [/^\/payments(\/|$)/, "payment"],
  [/^\/groups\/[^/]+\/schedule(\/|$)/, "schedule"],
  [/^\/groups\/[^/]+\/vendor-quotes(\/|$)/, "vendor"],
  [/^\/groups\/[^/]+\/orders(\/|$)/, "vendor"],
  [/^\/groups\/[^/]+\/purchases(\/|$)/, "vendor"],
  [/^\/groups(\/|$)/, "group"],
  [/^\/schedule(\/|$)/, "schedule"],
  // 案件なしの工程表（#291）
  [/^\/schedules\//, "schedule"],
  [/^\/p\//, "schedule"],
  [/^\/(rfqs|vendor-quotes|orders|purchases|payment-notices)(\/|$)/, "vendor"],
  [/^\/partners(\/|$)/, "partner"],
  [/^\/(account\/plan|legal)(\/|$)/, "plan"],
  [/^\/(account|choose|handoff)(\/|$)/, "users"], // handoff＝製品をまたぐ会社の切替（#151）
  [/^\/(login|signup|verify|forgot|reset-password|reopen|invite|oauth|welcome)(\/|$)/, "login"],
  [/^\/(terms|privacy)(\/|$)/, "other"],
  [/^\/updates(\/|$)/, "other"], // 更新情報（#204、ログイン前も読める）
  [/^\/(contact|support)(\/|$)/, null],
];

/** 画面のパス → 機能。feature＝束に入った／none＝画面が無い・画面に依らない（AI が選ぶ）／unmapped＝どの束にも入らない（管理アプリが数える） */
export type SupportFeatureOf = { kind: "feature"; feature: SupportFeature } | { kind: "none" } | { kind: "unmapped" };
export function supportFeatureOf(screen: string | null | undefined): SupportFeatureOf {
  const s = (screen ?? "").trim().split(/[?#]/)[0] ?? "";
  if (s === "" || s === "/") return { kind: "none" };
  for (const [re, f] of SUPPORT_FEATURE_ROUTES) if (re.test(s)) return f ? { kind: "feature", feature: f } : { kind: "none" };
  return { kind: "unmapped" };
}

// ---------------------------------------------------------------------------
// 緊急度（3段。§10.4）
// ---------------------------------------------------------------------------
export const SUPPORT_URGENCIES = {
  stop: { label: "業務が止まっている", short: "業務停止", rule: "見積・請求書が出せない・ログインできない・データが見えない" },
  deadline: { label: "期限がある", short: "期限あり", rule: "締め日・提出日・入金日が近いと書いてある" },
  normal: { label: "ふつう", short: "ふつう", rule: "ほか" },
} as const;
export type SupportUrgency = keyof typeof SUPPORT_URGENCIES;
export const SUPPORT_URGENCY_CODES = Object.keys(SUPPORT_URGENCIES) as SupportUrgency[];
export const SupportUrgency = z.enum(SUPPORT_URGENCY_CODES as [SupportUrgency, ...SupportUrgency[]]);
/** 通知を強くする（Web プッシュの urgency high）・一覧で朱の札 */
export const isUrgent = (u: SupportUrgency | null | undefined): boolean => u === "stop" || u === "deadline";

// ---------------------------------------------------------------------------
// 必要なもの（受付の見込み、6つ）と 解決のしかた（完了の実際、7つ）。§10.5
// ---------------------------------------------------------------------------
export const SUPPORT_NEEDS = {
  article: { label: "記事", rule: "ヘルプの記事で答えられる" },
  explain: { label: "説明", rule: "説明が要る（記事が無い）" },
  company_data: { label: "会社のデータ", rule: "その会社のデータを見ないと答えられない" },
  operation: { label: "操作の代行", rule: "運営が操作を代わる（無料期間の延長・消したものを戻す など）" },
  dev_fix: { label: "開発の修正", rule: "不具合で開発の修正が要る" },
  consider: { label: "検討", rule: "要望で、作るかどうかの検討が要る" },
} as const;
export type SupportNeed = keyof typeof SUPPORT_NEEDS;
export const SUPPORT_NEED_CODES = Object.keys(SUPPORT_NEEDS) as SupportNeed[];
export const SupportNeed = z.enum(SUPPORT_NEED_CODES as [SupportNeed, ...SupportNeed[]]);

export const SUPPORT_RESOLUTIONS = {
  article: "記事を案内した",
  explain: "説明した",
  company_data: "データを見て説明した",
  operation: "運営が操作した",
  dev_fix: "修正版を出した",
  consider: "要望として記録した",
  unresolved: "解決しないまま閉じた",
} as const;
export type SupportResolution = keyof typeof SUPPORT_RESOLUTIONS;
export const SUPPORT_RESOLUTION_CODES = Object.keys(SUPPORT_RESOLUTIONS) as SupportResolution[];
export const SupportResolution = z.enum(SUPPORT_RESOLUTION_CODES as [SupportResolution, ...SupportResolution[]]);

// ---------------------------------------------------------------------------
// タグ（§10.9）：運営が承認した一覧だけ。AI は一覧から2つまで選び、合うものが無ければ候補を1つ
// ---------------------------------------------------------------------------
export const SUPPORT_TAG_LIMITS = {
  /** 1つの機能に（統合したものは数えない） */
  perFeature: 20,
  /** 1件に AI が付ける数 */
  perTicketAi: 2,
  /** 名前の長さ */
  nameMax: 20,
} as const;
export const SupportTagName = z
  .string()
  .trim()
  .min(1, "タグの名前を書いてください")
  .max(SUPPORT_TAG_LIMITS.nameMax, `タグの名前は ${SUPPORT_TAG_LIMITS.nameMax}字までです`);
